import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getOrderStatus,
  getDailyProduction,
  getDelayedOrders,
  getOperatorPerformance,
  getCoilForecast,
  getStockMovements,
  getCuttingTolerance,
} from "../../api/chatbot";
import { getProductionOrderCostByOrderNo } from "../../api/cost";

const QUERIES = [
  { key: "order-status", label: "Sipariş Durumu", icon: "📦", needsInput: true, inputLabel: "Sipariş numarası", inputPlaceholder: "SO-1245" },
  { key: "daily-production", label: "Günlük Üretim Raporu", icon: "📊", needsInput: true, inputLabel: "Tarih", inputPlaceholder: "YYYY-MM-DD (boş = bugün)", inputType: "date" },
  { key: "delayed-orders", label: "Geciken Siparişler", icon: "⚠️", needsInput: false },
  { key: "operator-performance", label: "Operatör Performansı", icon: "👷", needsInput: true, inputLabel: "Kaç gün?", inputPlaceholder: "7", inputType: "number" },
  { key: "coil-forecast", label: "Sipariş Hammadde İhtiyacı", icon: "🔮", needsInput: true, inputLabel: "Sipariş no", inputPlaceholder: "SO-1245" },
  { key: "cost-report", label: "Fiili Maliyet", icon: "💰", needsInput: true, inputLabel: "İş Emri No", inputPlaceholder: "WO-8-60" },
  { key: "stock-movements", label: "Stok Hareketleri", icon: "↔", needsInput: false },
  { key: "cutting-tolerance", label: "Kesim Fazla Onay", icon: "✂", needsInput: false },
];

function StatusChip({ status }) {
  const map = {
    completed: { label: "Tamamlandı", color: "success" },
    in_progress: { label: "Devam Ediyor", color: "info" },
    pending: { label: "Bekliyor", color: "default" },
    cancelled: { label: "İptal", color: "error" },
  };
  const cfg = map[status] || { label: status || "—", color: "default" };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

function calcEarliestFinishDate(items) {
  // Tüm operasyonlardaki eta_hours'ları topla, +48 saat (2 gün) ekle
  let totalHours = 0;
  let hasAnyEta = false;
  for (const item of items) {
    for (const op of item.operations) {
      if (op.eta_hours && op.reel_kalan > 0) {
        totalHours += op.eta_hours;
        hasAnyEta = true;
      }
    }
  }
  if (!hasAnyEta) return null;
  totalHours += 48; // +2 gün
  const d = new Date();
  // 8 saatlik iş günü
  const workingDays = Math.ceil(totalHours / 8);
  d.setDate(d.getDate() + workingDays);
  return d.toISOString().slice(0, 10);
}

// Ürün adındaki parantez içi bilgiyi ekrandan gizle: "... TABAK (23 cm ...)" → "... TABAK"
function stripParen(name) {
  const s = String(name || "");
  const i = s.indexOf("(");
  return (i >= 0 ? s.slice(0, i) : s).trim();
}

// İş Emirleri ekranındaki tooltip'in aynısı: kalem üzerine gelince varyant bilgileri.
function itemTitleCell(item) {
  const details = Array.isArray(item?.variant_details) ? item.variant_details : [];
  const productName = item?.product_material_name || "";
  const label = stripParen(item?.product_name) || item?.order_no;
  const text = (
    <Typography component="span" variant="subtitle1" fontWeight={700}>{label}</Typography>
  );
  if (!details.length && !productName) return text;

  return (
    <Tooltip
      arrow
      placement="right"
      title={
        <div>
          {productName ? (
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{productName}</div>
          ) : null}
          {details.map((detail, index) => (
            <div key={`${detail?.label || "dim"}-${index}`} style={{ whiteSpace: "nowrap" }}>
              <span style={{ opacity: 0.75 }}>{detail?.label}:</span>{" "}
              <b>{detail?.value}{detail?.unit ? ` ${detail.unit}` : ""}</b>
            </div>
          ))}
        </div>
      }
    >
      <span style={{ cursor: "help", textDecoration: "underline dotted" }}>{text}</span>
    </Tooltip>
  );
}

function OrderStatusResult({ data }) {
  if (!data?.items?.length) return <Typography color="text.secondary">Sonuç bulunamadı.</Typography>;

  const earliestFinish = calcEarliestFinishDate(data.items);

  return (
    <Stack spacing={1}>
      {/* Sipariş başlığı */}
      <Stack direction="row" spacing={2} alignItems="center" mb={1} flexWrap="wrap">
        <Typography variant="h6" fontWeight={700}>{data.salesOrderNo}</Typography>
        {data.customerName && (
          <Typography variant="body2" color="text.secondary">— {data.customerName}</Typography>
        )}
        {earliestFinish && (
          <Chip
            label={`En erken bitiş: ${earliestFinish}`}
            color="info"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>

      {/* Kalem kartları */}
      {data.items.map((item, idx) => (
        <Card key={idx} variant="outlined" sx={{ mb: 1 }}>
          <CardContent sx={{ pb: "12px !important" }}>
            {/* Kalem başlığı */}
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5} flexWrap="wrap">
              {item.item_line_no !== null && (
                <Chip label={`Kalem ${item.item_line_no}`} size="small" variant="outlined" />
              )}
              {itemTitleCell(item)}
              <StatusChip status={item.status} />
              <Typography variant="body2" color="text.secondary">
                İş Emri: {item.order_no}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Planlanan: <strong>{item.planned_qty.toLocaleString("tr-TR")}</strong>
              </Typography>
              {item.due_date && (
                <Typography variant="body2" color="text.secondary">
                  Termin: <strong>{item.due_date}</strong>
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 100 }}>
                <LinearProgress
                  variant="determinate"
                  value={item.overall_pct}
                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption">{item.overall_pct}%</Typography>
              </Box>
            </Stack>

            {/* Operasyon tablosu */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>İşlem</TableCell>
                    <TableCell align="right">Hedef</TableCell>
                    <TableCell align="right">Yapılan</TableCell>
                    <TableCell align="right">Fark</TableCell>
                    <TableCell align="right">Reel kalan</TableCell>
                    <TableCell align="right">Hız (adet/sa)</TableCell>
                    <TableCell align="right">Tahmini Bitiş</TableCell>
                    <TableCell>İlerleme</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {item.operations.map((op, oi) => {
                    const pct = op.progress_pct ?? 0;
                    const isCompleted = op.finished;
                    const isActive = item.active_operation === op.operation_name && op.reel_kalan > 0;
                    return (
                      <TableRow key={oi} sx={isActive ? { bgcolor: "action.hover" } : undefined}>
                        <TableCell>
                          {op.operation_name}
                          {isActive && (
                            <Chip label="AKTİF" size="small" color="warning" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {op.target != null
                            ? `${op.target.toLocaleString("tr-TR")}${op.target_unit ? ` ${op.target_unit}` : ""}`
                            : op.qty_target.toLocaleString("tr-TR")}
                        </TableCell>
                        <TableCell align="right">{op.qty_done.toLocaleString("tr-TR")}</TableCell>
                        <TableCell align="right">{op.fark != null ? op.fark.toLocaleString("tr-TR") : "—"}</TableCell>
                        <TableCell align="right">{op.reel_kalan != null ? op.reel_kalan.toLocaleString("tr-TR") : "—"}</TableCell>
                        <TableCell align="right">
                          {op.avg_speed ? op.avg_speed.toLocaleString("tr-TR") : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          {isCompleted ? (
                            <Chip label="Tamamlandı" size="small" color="success" />
                          ) : op.eta_hours ? (
                            <Typography variant="body2" fontWeight={600}>~{op.eta_hours} sa</Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">Hız girilmemiş</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{ flex: 1, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption">{pct}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {op.finished ? (
                            <Chip label="Bitti" size="small" color="error" />
                          ) : op.qty_done > 0 ? (
                            <Chip label="Başlandı" size="small" color="success" />
                          ) : (
                            <Chip label="Başlanmadı" size="small" color="warning" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function DailyProductionResult({ data }) {
  if (!data) return null;
  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={700}>Tarih: {data.date}</Typography>

      {data.summary.length > 0 && (
        <>
          <Typography variant="subtitle2">Özet (operasyona göre)</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Operasyon</TableCell>
                  <TableCell align="right">Toplam Üretim</TableCell>
                  <TableCell align="right">Operatör Sayısı</TableCell>
                  <TableCell align="right">İş Emri Sayısı</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.summary.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{s.operation_code}</TableCell>
                    <TableCell align="right">{s.total_qty.toLocaleString("tr-TR")}</TableCell>
                    <TableCell align="right">{s.operator_count}</TableCell>
                    <TableCell align="right">{s.order_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {data.details.length > 0 && (
        <>
          <Typography variant="subtitle2">Detay</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>İş Emri</TableCell>
                  <TableCell>Operasyon</TableCell>
                  <TableCell>Operatör</TableCell>
                  <TableCell align="right">Miktar</TableCell>
                  <TableCell align="right">Onay Sayısı</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.details.map((d, i) => (
                  <TableRow key={i}>
                    <TableCell>{d.order_no}</TableCell>
                    <TableCell>{d.operation_code}</TableCell>
                    <TableCell>{d.operator_name}</TableCell>
                    <TableCell align="right">{d.total_qty.toLocaleString("tr-TR")}</TableCell>
                    <TableCell align="right">{d.approval_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {data.details.length === 0 && <Typography color="text.secondary">Bu tarihte üretim kaydı yok.</Typography>}
    </Stack>
  );
}

function DelayedOrdersResult({ data }) {
  if (!data) return null;
  if (!data.orders.length) return <Typography color="success.main" fontWeight={600}>Geciken sipariş yok!</Typography>;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1" fontWeight={700} color="error">
        {data.count} adet geciken sipariş
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>İş Emri</TableCell>
              <TableCell>Sipariş No</TableCell>
              <TableCell>Müşteri</TableCell>
              <TableCell>Ürün</TableCell>
              <TableCell>Termin</TableCell>
              <TableCell align="right">Gecikme (gün)</TableCell>
              <TableCell align="right">Planlanan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.orders.map((o, i) => (
              <TableRow key={i} sx={o.delay_days > 7 ? { bgcolor: "error.50" } : undefined}>
                <TableCell>{o.order_no}</TableCell>
                <TableCell>{o.sales_order_no || "—"}</TableCell>
                <TableCell>{o.customer_name || "—"}</TableCell>
                <TableCell>{o.product_name || "—"}</TableCell>
                <TableCell>{o.due_date}</TableCell>
                <TableCell align="right">
                  <Chip label={`${o.delay_days} gün`} size="small" color={o.delay_days > 7 ? "error" : "warning"} />
                </TableCell>
                <TableCell align="right">{o.planned_qty.toLocaleString("tr-TR")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function OperatorPerformanceResult({ data }) {
  if (!data) return null;
  if (!data.operators.length) return <Typography color="text.secondary">Bu dönemde üretim kaydı yok.</Typography>;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1" fontWeight={700}>Son {data.period_days} gün</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Operatör</TableCell>
              <TableCell align="right">Toplam Üretim</TableCell>
              <TableCell align="right">Onay Sayısı</TableCell>
              <TableCell>Operasyonlar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.operators.map((op, i) => (
              <TableRow key={i}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{op.name}</TableCell>
                <TableCell align="right">{op.total_production.toLocaleString("tr-TR")}</TableCell>
                <TableCell align="right">{op.approval_count}</TableCell>
                <TableCell>{op.operations}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function fmtNum(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
}

function RequirementCellPair({ primary, primaryUnit, secondary, secondaryUnit, missing, missingSecondary }) {
  const hasMissing = Number(missing) > 0 || Number(missingSecondary) > 0;
  return (
    <Stack spacing={0.2}>
      {primary != null && (
        <Typography variant="body2">
          {fmtNum(primary)} {primaryUnit}
        </Typography>
      )}
      {secondary != null && (
        <Typography variant="caption" color="text.secondary">
          {fmtNum(secondary)} {secondaryUnit}
        </Typography>
      )}
      {hasMissing && (
        <Chip label="EKSİK" size="small" color="error" sx={{ alignSelf: "flex-start", fontSize: 10 }} />
      )}
    </Stack>
  );
}

function MatchInfoBlock({ matchInfo }) {
  if (!matchInfo) return "—";

  const noteLower = String(matchInfo.note || "").toLowerCase();
  const isStockScanSkipped =
    noteLower.includes("stok taramasi yapilmadi") ||
    noteLower.includes("stok taraması yapılmadı");

  const summaryBits = [
    matchInfo.gsm ? `${matchInfo.gsm} g` : null,
    matchInfo.paper_kind || null,
    matchInfo.bobin_eni_cm ? `${matchInfo.bobin_eni_cm} cm` : null,
    matchInfo.pantone ? `Pantone: ${matchInfo.pantone}` : null,
    matchInfo.matched_lots != null ? `${matchInfo.matched_lots} lot` : null,
  ].filter(Boolean);

  return (
    <Stack spacing={0.4}>
      {summaryBits.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          {summaryBits.join(" · ")}
        </Typography>
      ) : null}

      {matchInfo.note ? (
        <Typography
          variant="caption"
          sx={{
            display: "inline-block",
            color: isStockScanSkipped ? "info.dark" : "text.primary",
            bgcolor: isStockScanSkipped ? "info.lighter" : "action.hover",
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            border: isStockScanSkipped ? "1px solid" : "none",
            borderColor: isStockScanSkipped ? "info.light" : "transparent",
          }}
        >
          {matchInfo.note}
        </Typography>
      ) : null}

      {matchInfo.alt_available_kg > 0 ? (
        <Typography variant="caption" color="warning.dark">
          Alternatif stok: {fmtNum(matchInfo.alt_available_kg)} kg
        </Typography>
      ) : null}

      {matchInfo.alt_matched_lots > 0 ? (
        <Typography variant="caption" color="warning.main" fontWeight={600}>
          Ayni renkte alternatif stoklar da bulundu.
        </Typography>
      ) : null}
    </Stack>
  );
}

function CoilForecastResult({ data }) {
  if (!data) return null;
  if (!data.items?.length) {
    return <Typography color="text.secondary">Bu sipariş için kalem bulunamadı.</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" fontWeight={700}>{data.order_no}</Typography>
        {data.customer_name && (
          <Typography variant="body2" color="text.secondary">— {data.customer_name}</Typography>
        )}
      </Stack>

      {data.items.map((item, idx) => (
        <Card key={idx} variant="outlined">
          <CardContent sx={{ pb: "12px !important" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5} flexWrap="wrap">
              {item.line_no != null && (
                <Chip label={`Kalem ${item.line_no}`} size="small" variant="outlined" />
              )}
              <Typography variant="subtitle1" fontWeight={700}>
                {item.material_code} — {item.material_name}
              </Typography>
              {item.variant_display_name && (
                <Typography variant="body2" color="text.secondary">
                  {item.variant_display_name}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="body2">
                Miktar: <strong>{fmtNum(item.qty)}</strong>
              </Typography>
            </Stack>

            {item.requirements.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Varyant bilgilerinden hammadde ihtiyacı hesaplanamadı (gramaj/kalıp/çap/bobin eni kontrol edin).
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Malzeme</TableCell>
                      <TableCell align="right">Gereken</TableCell>
                      <TableCell align="right">Stokta</TableCell>
                      <TableCell align="right">Eksik</TableCell>
                      <TableCell>Eşleşme</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.requirements.map((r, ri) => {
                      const isPaper = r.category === "paper";
                      const isInk = r.category === "ink";
                      const missingPrimary = isPaper ? r.missing_mt : (r.missing_kg ?? r.missing_qty);
                      const rowDanger = (missingPrimary || 0) > 0;
                      return (
                        <TableRow key={ri} sx={rowDanger ? { bgcolor: "error.50" } : undefined}>
                          <TableCell>{r.material_name}</TableCell>
                          <TableCell align="right">
                            {isPaper ? (
                              <RequirementCellPair
                                primary={r.needed_mt}
                                primaryUnit="mt"
                                secondary={r.needed_kg}
                                secondaryUnit="kg"
                              />
                            ) : isInk ? (
                              <RequirementCellPair
                                primary={r.needed_kg}
                                primaryUnit="kg"
                              />
                            ) : (
                              <RequirementCellPair
                                primary={r.needed_qty}
                                primaryUnit={r.needed_unit || "adet"}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {isPaper ? (
                              <RequirementCellPair
                                primary={r.available_mt}
                                primaryUnit="mt"
                                secondary={r.available_kg}
                                secondaryUnit="kg"
                              />
                            ) : isInk ? (
                              <RequirementCellPair
                                primary={r.available_kg}
                                primaryUnit="kg"
                              />
                            ) : (
                              <RequirementCellPair
                                primary={r.available_qty}
                                primaryUnit={r.needed_unit || "adet"}
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {isPaper ? (
                              <RequirementCellPair
                                primary={r.missing_mt}
                                primaryUnit="mt"
                                secondary={r.missing_kg}
                                secondaryUnit="kg"
                                missing={r.missing_mt}
                                missingSecondary={r.missing_kg}
                              />
                            ) : isInk ? (
                              <RequirementCellPair
                                primary={r.missing_kg}
                                primaryUnit="kg"
                                missing={r.missing_kg}
                              />
                            ) : (
                              <RequirementCellPair
                                primary={r.missing_qty}
                                primaryUnit={r.needed_unit || "adet"}
                                missing={r.missing_qty}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <MatchInfoBlock matchInfo={r.match_info} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

const COST_TYPE_LABELS = {
  paper: "Kağıt", ink: "Boya", lak: "Lak", box: "Koli",
  shrink: "Shrink", label: "Etiket", other: "Diğer",
};

function fmtMoney(n, currency = "TRY") {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function CostReportResult({ data }) {
  if (!data) return null;
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" fontWeight={700}>{data.order_no}</Typography>
        <Chip label={`Planlanan: ${fmtNum(data.planned_qty)} adet`} size="small" />
        {data.missing_cost_count > 0 && (
          <Chip label={`${data.missing_cost_count} kalem fiyatsız`} color="warning" size="small" />
        )}
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary">Toplam Maliyet</Typography>
          {Object.entries(data.totals_by_currency || {}).map(([cur, val]) => (
            <Typography key={cur} variant="h5" color="primary">{fmtMoney(val, cur)}</Typography>
          ))}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary">Birim Ürün Maliyeti</Typography>
          {Object.entries(data.unit_cost_by_currency || {}).map(([cur, val]) => (
            <Typography key={cur} variant="h5" color="secondary">{fmtMoney(val, cur)} / adet</Typography>
          ))}
        </Grid>
      </Grid>

      {data.by_material_type?.length > 0 && (
        <>
          <Typography variant="subtitle2">Hammadde Türüne Göre Dağılım</Typography>
          <Grid container spacing={1}>
            {data.by_material_type.map((row, idx) => (
              <Grid item xs={6} sm={4} md={3} key={idx}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {COST_TYPE_LABELS[row.raw_material_type] || row.raw_material_type}
                  </Typography>
                  <Typography variant="h6">{fmtMoney(row.total, row.currency)}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <Typography variant="subtitle2">Tüketim Detayı ({data.total_lines} satır)</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>İşlem</TableCell>
              <TableCell>Malzeme Kodu</TableCell>
              <TableCell>Malzeme Adı</TableCell>
              <TableCell>Lot No</TableCell>
              <TableCell align="right">Miktar</TableCell>
              <TableCell>Birim</TableCell>
              <TableCell align="right">Birim Fiyat</TableCell>
              <TableCell align="right">Tutar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((it, idx) => (
              <TableRow key={idx} sx={{ bgcolor: it.has_cost ? undefined : "warning.light" }}>
                <TableCell>{it.operation_code || "—"}</TableCell>
                <TableCell>{it.material_code}</TableCell>
                <TableCell>
                  {it.material_name}
                  {it.variant_display_name && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {it.variant_display_name}
                    </Typography>
                  )}
                </TableCell>
                <TableCell><code>{it.lot_no}</code></TableCell>
                <TableCell align="right">{fmtNum(it.qty)}</TableCell>
                <TableCell>{it.uom}</TableCell>
                <TableCell align="right">
                  {it.unit_cost !== null
                    ? fmtMoney(it.unit_cost, it.currency)
                    : <Chip label="Fiyat yok" color="warning" size="small" />}
                </TableCell>
                <TableCell align="right">
                  <strong>{it.line_cost !== null ? fmtMoney(it.line_cost, it.currency) : "—"}</strong>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function StockStatusResult({ data }) {
  if (!data) return null;
  if (!data.balances.length) return <Typography color="text.secondary">Stok kaydı bulunamadı.</Typography>;

  return (
    <Stack spacing={1}>
      {data.order_no && <Typography variant="subtitle1" fontWeight={700}>İş Emri: {data.order_no}</Typography>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Malzeme</TableCell>
              <TableCell>Birim</TableCell>
              {data.order_no && <TableCell>Lot</TableCell>}
              <TableCell align="right">Miktar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.balances.map((b, i) => (
              <TableRow key={i}>
                <TableCell>{b.material_name}</TableCell>
                <TableCell>{b.unit}</TableCell>
                {data.order_no && <TableCell>{b.lot_no || "—"}</TableCell>}
                <TableCell align="right">{b.on_hand_qty.toLocaleString("tr-TR")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function formatMovementDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function stockMovementPresentation(movement) {
  if (movement?.is_reversal) {
    return {
      label: movement.tx_type === "receipt" ? "Ters Giriş" : movement.tx_type === "issue" ? "Ters Çıkış" : "Ters Düzeltme",
      color: "warning",
    };
  }
  if (movement?.tx_type === "receipt") return { label: "Giriş", color: "success" };
  if (movement?.tx_type === "issue") return { label: "Çıkış", color: "error" };
  return { label: "Düzeltme", color: "warning" };
}

function stockMovementReferenceLabel(refType) {
  const labels = {
    production_operation: "Üretim hareketi",
    production_operation_correction: "Üretim düzeltmesi",
    approval_reversal: "Onay ters hareketi",
    raw_material_receive: "Stok girişi",
    inventory_adjust: "Stok düzeltmesi",
    production_order_shipment: "Sevk",
    paper_reservation: "Rezervasyon",
    paper_reservation_cancel: "Rezervasyon iptali",
    paper_reservation_update: "Rezervasyon güncellemesi",
    ink_mix: "Mürekkep karışımı",
    fason_order: "Fason hareketi",
    quality_reject: "Kalite reddi",
  };
  return labels[refType] || "Operasyon dışı hareket";
}

function StockMovementsResult({ data }) {
  const movements = Array.isArray(data?.movements) ? data.movements : [];
  if (!movements.length) return <Typography color="text.secondary">Stok hareketi bulunamadı.</Typography>;

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle1" fontWeight={700}>
        {movements.length.toLocaleString("tr-TR")} stok hareketi · eski tarihten yeni tarihe
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 1250 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Tarih / Saat</TableCell>
              <TableCell>İşlemi Yapan</TableCell>
              <TableCell>Hareket</TableCell>
              <TableCell>Malzeme</TableCell>
              <TableCell>Lot No</TableCell>
              <TableCell>İş Emri</TableCell>
              <TableCell>Operasyon</TableCell>
              <TableCell align="right">Miktar</TableCell>
              <TableCell>Açıklama</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.map((movement, index) => {
              const presentation = stockMovementPresentation(movement);
              const signedQty = Number(movement.signed_qty || 0);
              const operationLabel = movement.op_no != null
                ? `OP ${movement.op_no} · ${movement.operation_name || movement.operation_code || "Operasyon"}`
                : "Operasyon Dışı";
              return (
                <TableRow key={movement.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatMovementDateTime(movement.created_at)}</TableCell>
                  <TableCell>{movement.actor_name || "Sistem"}</TableCell>
                  <TableCell><Chip label={presentation.label} color={presentation.color} size="small" /></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{movement.material_code || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">{movement.material_name || "—"}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{movement.lot_no || "—"}</TableCell>
                  <TableCell>{movement.order_no || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={movement.op_no != null ? 700 : 400}>{operationLabel}</Typography>
                    {movement.work_center_name || movement.work_center_code ? (
                      <Typography variant="caption" color="text.secondary">
                        {[movement.work_center_code, movement.work_center_name].filter(Boolean).join(" · ")}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 800, whiteSpace: "nowrap", color: signedQty < 0 ? "error.main" : signedQty > 0 ? "success.main" : "text.primary" }}
                  >
                    {signedQty > 0 ? "+" : ""}{signedQty.toLocaleString("tr-TR", { maximumFractionDigits: 4 })} {movement.uom || ""}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{movement.note || stockMovementReferenceLabel(movement.ref_type)}</Typography>
                    {movement.note ? (
                      <Typography variant="caption" color="text.secondary">{stockMovementReferenceLabel(movement.ref_type)}</Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function CuttingToleranceResult({ data }) {
  const items = Array.isArray(data?.items) ? data.items : [];
  if (!items.length) return <Typography color="text.secondary">Fazla onay kaydı bulunamadı.</Typography>;

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle1" fontWeight={700}>
        {items.length.toLocaleString("tr-TR")} kayıt · toplam{" "}
        {Number(data?.totalToleranceSheets || 0).toLocaleString("tr-TR")} tabaka fazla onay
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <TableCell>Parti No</TableCell>
              <TableCell>Müşteri Kodu</TableCell>
              <TableCell>Müşteri Adı</TableCell>
              <TableCell>İş Emri No</TableCell>
              <TableCell>Onay Tarihi</TableCell>
              <TableCell>Onay Veren</TableCell>
              <TableCell align="right">Fazla Tabaka</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row, index) => (
              <TableRow key={index} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>{row.lot_no || "—"}</TableCell>
                <TableCell>{row.customer_code || "—"}</TableCell>
                <TableCell>{row.customer_name || "—"}</TableCell>
                <TableCell>{row.work_order_no || "—"}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatMovementDateTime(row.approved_at)}</TableCell>
                <TableCell>{row.approved_by || "—"}</TableCell>
                <TableCell align="right">
                  <Chip label={Number(row.tolerance_sheets || 0).toLocaleString("tr-TR")} color="warning" size="small" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

const RESULT_RENDERERS = {
  "order-status": OrderStatusResult,
  "daily-production": DailyProductionResult,
  "delayed-orders": DelayedOrdersResult,
  "operator-performance": OperatorPerformanceResult,
  "coil-forecast": CoilForecastResult,
  "stock-status": StockStatusResult,
  "cost-report": CostReportResult,
  "stock-movements": StockMovementsResult,
  "cutting-tolerance": CuttingToleranceResult,
};

function currentUserRole() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.role || null;
  } catch {
    return null;
  }
}

// Yönetici, Stok Hareketleri ve Kesim Fazla Onay hariç diğer tüm raporları görebilir.
const YONETICI_HIDDEN_QUERY_KEYS = new Set(["stock-movements", "cutting-tolerance"]);
// Günlük Üretim Raporu, Operatör Performansı ve Fiili Maliyet "yonetici"
// rolüne özel; admin de görebilir.
const YONETICI_ONLY_QUERY_KEYS = new Set(["daily-production", "operator-performance", "cost-report"]);

export default function ChatbotPage() {
  const navigate = useNavigate();
  const role = currentUserRole();
  // depocu sadece Stok Hareketleri'ni görebilir.
  const visibleQueries =
    role === "yonetici"
      ? QUERIES.filter((q) => !YONETICI_HIDDEN_QUERY_KEYS.has(q.key))
      : role === "depocu"
        ? QUERIES.filter((q) => q.key === "stock-movements")
        : role === "admin"
          ? QUERIES
          : QUERIES.filter((q) => !YONETICI_ONLY_QUERY_KEYS.has(q.key));

  const [activeQuery, setActiveQuery] = useState(null);
  const [inputVal, setInputVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [resultKey, setResultKey] = useState(null);
  const [error, setError] = useState(null);
  const [stockMovementFilters, setStockMovementFilters] = useState({ orderNo: "", lotNo: "" });
  const [cuttingToleranceFilters, setCuttingToleranceFilters] = useState({ dateFrom: "", dateTo: "" });

  const handleSelect = (q) => {
    setActiveQuery(q);
    setInputVal("");
    setResult(null);
    setResultKey(null);
    setError(null);
    setStockMovementFilters({ orderNo: "", lotNo: "" });
    setCuttingToleranceFilters({ dateFrom: "", dateTo: "" });
  };

  const handleSubmit = async () => {
    if (!activeQuery) return;
    const q = activeQuery;

    setLoading(true);
    setError(null);
    setResult(null);
    setResultKey(null);

    try {
      let data;
      switch (q.key) {
        case "order-status":
          if (!inputVal.trim()) { setError("Sipariş numarası girin (örn: SO-1245)"); setLoading(false); return; }
          data = await getOrderStatus(inputVal.trim());
          break;
        case "daily-production":
          data = await getDailyProduction(inputVal.trim() || undefined);
          break;
        case "delayed-orders":
          data = await getDelayedOrders();
          break;
        case "operator-performance":
          data = await getOperatorPerformance(inputVal.trim() ? Number(inputVal) : 7);
          break;
        case "coil-forecast":
          if (!inputVal.trim()) { setError("Sipariş numarası girin (örn: SO-1245)"); setLoading(false); return; }
          data = await getCoilForecast(inputVal.trim());
          break;
        case "cost-report":
          if (!inputVal.trim()) { setError("İş emri numarası girin (örn: WO-8-60)"); setLoading(false); return; }
          data = await getProductionOrderCostByOrderNo(inputVal.trim());
          break;
        case "stock-movements": {
          const orderNo = stockMovementFilters.orderNo.trim();
          const lotNo = stockMovementFilters.lotNo.trim();
          if (!orderNo && !lotNo) {
            setError("İş emri no veya lot no alanlarından en az birini girin.");
            setLoading(false);
            return;
          }
          data = await getStockMovements({ orderNo, lotNo });
          break;
        }
        case "cutting-tolerance":
          data = await getCuttingTolerance({
            dateFrom: cuttingToleranceFilters.dateFrom.trim(),
            dateTo: cuttingToleranceFilters.dateTo.trim(),
          });
          break;
        default:
          break;
      }
      setResult(data);
      setResultKey(q.key);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message || "Bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const Renderer = resultKey ? RESULT_RENDERERS[resultKey] : null;

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} mb={3}>
        Üretim Asistanı
      </Typography>

      <Grid container spacing={2} mb={3}>
        {visibleQueries.map((q) => (
          <Grid item xs={6} sm={4} md={2} key={q.key}>
            <Button
              fullWidth
              variant={activeQuery?.key === q.key ? "contained" : "outlined"}
              onClick={() => handleSelect(q)}
              sx={{
                py: 2,
                flexDirection: "column",
                gap: 0.5,
                textTransform: "none",
                fontWeight: activeQuery?.key === q.key ? 700 : 500,
              }}
            >
              <span style={{ fontSize: 24 }}>{q.icon}</span>
              <span style={{ fontSize: 13 }}>{q.label}</span>
            </Button>
          </Grid>
        ))}
        {role === "admin" && (
          <Grid item xs={6} sm={4} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate("/paper-reservations")}
              sx={{
                py: 2,
                flexDirection: "column",
                gap: 0.5,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              <span style={{ fontSize: 24 }}>▣</span>
              <span style={{ fontSize: 13 }}>Rezervasyonlar</span>
            </Button>
          </Grid>
        )}
      </Grid>

      {activeQuery && (
        <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            {activeQuery.icon} {activeQuery.label}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "flex-end" }}>
            {activeQuery.key === "stock-movements" ? (
              <>
                <TextField
                  label="İş Emri No"
                  placeholder="WO-8-60"
                  value={stockMovementFilters.orderNo}
                  onChange={(e) => setStockMovementFilters((prev) => ({ ...prev, orderNo: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  size="small"
                  sx={{ minWidth: 240 }}
                />
                <TextField
                  label="Lot No"
                  placeholder="Lot numarasının tamamı veya bir kısmı"
                  value={stockMovementFilters.lotNo}
                  onChange={(e) => setStockMovementFilters((prev) => ({ ...prev, lotNo: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  size="small"
                  sx={{ minWidth: 280 }}
                />
              </>
            ) : activeQuery.key === "cutting-tolerance" ? (
              <>
                <TextField
                  label="Başlangıç Tarihi"
                  type="date"
                  value={cuttingToleranceFilters.dateFrom}
                  onChange={(e) => setCuttingToleranceFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  size="small"
                  sx={{ minWidth: 200 }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  label="Bitiş Tarihi"
                  type="date"
                  value={cuttingToleranceFilters.dateTo}
                  onChange={(e) => setCuttingToleranceFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  size="small"
                  sx={{ minWidth: 200 }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </>
            ) : activeQuery.needsInput && (
              <TextField
                label={activeQuery.inputLabel}
                placeholder={activeQuery.inputPlaceholder}
                type={activeQuery.inputType || "text"}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                size="small"
                sx={{ minWidth: 260 }}
                slotProps={activeQuery.inputType === "date" ? { inputLabel: { shrink: true } } : undefined}
              />
            )}
            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "Sorgula"}
            </Button>
          </Stack>
        </Paper>
      )}

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: "error.50", border: "1px solid", borderColor: "error.200" }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {Renderer && result && (
        <Paper sx={{ p: 3 }} variant="outlined">
          <Renderer data={result} />
        </Paper>
      )}
    </Box>
  );
}
